import { useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { stripeAPI } from '../../api/services/stripe'
import { PageTransactions } from '../../api/services/stripe/types.ts'
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs.tsx'
import Card from '../../components/common/Card/Card.tsx'
import Loader from '../../components/common/Loader/Loader.tsx'
import Table from '../../components/common/Table/Table.tsx'
import { setTableActions, setTableColumns } from '../../utils/tableSettings.tsx'

const Transactions = () => {
  const [activeItem, setActiveItem] = useState('charges')
  const transactionsType = activeItem === 'payouts' ? 'payouts' : 'charges'

  const fetchTransactions = async ({
    pageParam
  }: {
    pageParam?: string
  }): Promise<PageTransactions> => {
    const response = await stripeAPI.getStripeTransactions(
      pageParam,
      transactionsType
    )
    return {
      data: response.data.data,
      hasMore: response.data.has_more,
      lastTransactionId: response.data.data[response.data.data.length - 1]?.id
    }
  }

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery<PageTransactions>({
    queryKey: ['transactions', activeItem],
    queryFn: fetchTransactions,
    keepPreviousData: true,

    getNextPageParam: (lastPage: PageTransactions) => {
      return lastPage.hasMore ? lastPage.lastTransactionId : undefined
    }
  })

  const observerElemRef = useRef<HTMLDivElement | null>(null)

  // Intersection Observer to detect when the last item (sentry) is visible
  const observerCallback = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries
      if (entry.isIntersecting && hasNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage]
  )

  useEffect(() => {
    const observer = new IntersectionObserver(observerCallback, {
      threshold: 1.0
    })

    if (observerElemRef.current) {
      observer.observe(observerElemRef.current)
    }

    return () => {
      if (observerElemRef.current) {
        observer.unobserve(observerElemRef.current)
      }
    }
  }, [observerCallback])

  if (isError) {
    return <div>Wystąpił błąd podczas zapytania na serwer</div>
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const allTransactions = data?.pages.flatMap((page) => page.data) || []
  const filteredTransactions = allTransactions.filter(
    (transaction) => transaction.type !== 'refund'
  )

  const columns = setTableColumns(transactionsType)
  const actions = setTableActions(transactionsType)

  return (
    <>
      <Breadcrumbs pageName="Transakcje" breadcrumbs={[]} />

      <Card>
        <Card.Body>
          {isLoading && (
            <Loader label={'Pobieranie danych o transakcjach...'} />
          )}

          {!isLoading && (
            <div className="flex justify-center gap-6 py-4">
              <button
                className={`relative px-4 py-2 text-gray-500 font-medium text-lg transition duration-300 
          ${activeItem === 'charges' ? 'text-blue-600' : ''}
        `}
                onClick={() => setActiveItem('charges')}
              >
                Wpłaty
                <span
                  className={`absolute left-0 bottom-0 h-0.5 w-full bg-blue-600 transition-all duration-300 ease-in-out 
            ${activeItem === 'charges' ? 'block' : 'invisible scale-x-0'}
          `}
                ></span>
              </button>
              <button
                className={`relative px-4 py-2 text-gray-500 font-medium text-lg transition duration-300 
          ${activeItem === 'payouts' ? 'text-blue-600' : ''}`}
                onClick={() => setActiveItem('payouts')}
              >
                Wypłaty
                <span
                  className={`absolute left-0 bottom-0 h-0.5 w-full bg-blue-600 transition-all duration-300 ease-in-out 
            ${activeItem === 'payouts' ? 'block' : 'invisible scale-x-0'}
          `}
                ></span>
              </button>
            </div>
          )}

          {data && !isLoading && (
            <div className="min-w-200 h-180 overflow-y-auto border border-gray-300 dark:border-gray-600 my-4 p-4">
              <Table
                key={JSON.stringify(filteredTransactions)}
                columns={columns}
                data={filteredTransactions}
                actions={actions}
              />
              <div
                ref={observerElemRef}
                style={{
                  visibility: hasNextPage ? 'visible' : 'hidden',
                  height: '1px'
                }}
              />
              {/* Loader (Spinner) when fetching next page */}
              {isFetchingNextPage && (
                <div className="text-center py-4">
                  <span className="loader">Pobieram więcej...</span>
                </div>
              )}

              {/* Show message when there's no more data */}
              {!hasNextPage && !isLoading && (
                <div className="text-center py-4">
                  <span>Brak więcej transakcji do pobrania.</span>
                </div>
              )}
            </div>
          )}

          {allTransactions && !isLoading && (
            <div>Pobrano {allTransactions.length} transakcji</div>
          )}
        </Card.Body>
      </Card>
    </>
  )
}

export default Transactions
